import React, { useState, useCallback, useEffect } from 'react';
import { 
  Image, 
  View, 
  StyleSheet, 
  ImageStyle, 
  ViewStyle, 
  ActivityIndicator,
  Text 
} from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { imageCache } from '@/utils/admin/imageCache';

interface LazyImageProps {
  source: { uri: string };
  style?: ImageStyle;
  containerStyle?: ViewStyle;
  resizeMode?: 'cover' | 'contain' | 'stretch' | 'repeat' | 'center';
  placeholder?: React.ReactNode;
  errorPlaceholder?: React.ReactNode;
  onLoad?: () => void;
  onError?: () => void;
}

export const LazyImage: React.FC<LazyImageProps> = ({
  source,
  style,
  containerStyle,
  resizeMode = 'cover',
  placeholder,
  errorPlaceholder,
  onLoad,
  onError,
}) => {
  const [loading, setLoading] = useState(!imageCache.isCached(source.uri));
  const [error, setError] = useState(false);

  // Check cache on mount and when URI changes
  useEffect(() => {
    if (imageCache.isCached(source.uri)) {
      setLoading(false);
      setError(false);
    } else {
      setLoading(true);
      setError(false);
      imageCache.markAsLoading(source.uri);
    }
  }, [source.uri]);

  const handleLoad = useCallback(() => {
    setLoading(false);
    setError(false);
    imageCache.markAsLoaded(source.uri);
    onLoad?.();
  }, [onLoad, source.uri]);

  const handleError = useCallback(() => {
    setLoading(false);
    setError(true);
    onError?.();
  }, [onError]);

  const handleLoadStart = useCallback(() => {
    if (!imageCache.isCached(source.uri)) {
      setLoading(true);
      setError(false);
      imageCache.markAsLoading(source.uri);
    }
  }, [source.uri]);

  const renderPlaceholder = () => {
    if (error) {
      return errorPlaceholder || (
        <View style={styles.placeholder}>
          <Text style={styles.errorText}>Failed to load image</Text>
        </View>
      );
    }

    if (loading) {
      return placeholder || (
        <View style={styles.placeholder}>
          <ActivityIndicator 
            size="small" 
            color={ADMIN_COLORS.PRIMARY} 
          />
        </View>
      );
    }

    return null;
  };

  return (
    <View style={[styles.container, style, containerStyle]}>
      {renderPlaceholder()}
      <Image
        source={source}
        style={[
          style,
          loading && styles.hidden,
          error && styles.hidden,
        ]}
        resizeMode={resizeMode}
        onLoad={handleLoad}
        onError={handleError}
        onLoadStart={handleLoadStart}
        // Performance optimizations
        progressiveRenderingEnabled={true}
        fadeDuration={200}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  placeholder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    justifyContent: 'center',
    alignItems: 'center',
  },
  hidden: {
    opacity: 0,
  },
  errorText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
  },
});
